import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  QrCode, 
  Building2, 
  Banknote, 
  ShieldCheck, 
  ArrowRight, 
  Info,
  CheckCircle2,
  Loader2,
  ChevronLeft
} from "lucide-react";
import { api } from "../services/api";

const PRIMARY = "#363C90";

const methods = [
  { id: "UPI", icon: QrCode, label: "UPI", color: "text-blue-600", bg: "bg-blue-50" },
  { id: "Bank Transfer", icon: Building2, label: "Bank Transfer", color: "text-purple-600", bg: "bg-purple-50" },
  { id: "Cash", icon: Banknote, label: "Cash", color: "text-emerald-600", bg: "bg-emerald-50" },
];

const Payment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = location.state || {};

  const [selected, setSelected] = useState<string | null>(null);
  const [upiId, setUpiId] = useState("");
  const [accHolder, setAccHolder] = useState("");
  const [accNumber, setAccNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [loading, setLoading] = useState(false);

  // --- LOGIC (UNTOUCHED) ---
  const isFormValid = () => {
    if (selected === "UPI") return upiId.includes("@");
    if (selected === "Bank Transfer") return accHolder && accNumber.length > 8 && bankName && ifsc.length === 11;
    if (selected === "Cash") return true;
    return false;
  };

  const confirmSell = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("user_token");
      if (!token) throw new Error("Please login to continue");
      let paymentDetails: any = {};
      if (selected === "UPI") paymentDetails = { upi_id: upiId };
      if (selected === "Bank Transfer") {
        paymentDetails = { account_holder: accHolder, account_number: accNumber, bank_name: bankName, ifsc: ifsc };
      }
      const res: any = await api.postAuth("/sell-order", {
        variant_id: params.variantId,
        address_id: params.addressId,
        final_price: params.finalPrice,
        pickup_slot: params.pickupSlot,
        payment_method: selected,
        payment_details: paymentDetails,
        global_answers: typeof params.globalAnswers === 'string' ? JSON.parse(params.globalAnswers) : params.globalAnswers,
        defect_answers: typeof params.defectAnswers === 'string' ? JSON.parse(params.defectAnswers || "[]") : params.defectAnswers,
        functional_answers: typeof params.functionalAnswers === 'string' ? JSON.parse(params.functionalAnswers || "[]") : params.functionalAnswers,
        accessory_answers: typeof params.accessoryAnswers === 'string' ? JSON.parse(params.accessoryAnswers || "[]") : params.accessoryAnswers,
        device_age: typeof params.deviceAge === 'string' ? JSON.parse(params.deviceAge || "[]") : params.deviceAge,
      }, token);
      navigate("/sell/thankyou", { state: { orderId: res.order_id } });
    } catch (e: any) {
      alert(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      
      {/* --- DASHBOARD HEADER --- */}
      <div className="bg-white border-b border-slate-100 py-6 px-6">
        <div className="max-w-[1000px] mx-auto flex items-center justify-between">
           <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight leading-none">Payout Method</h1>
              <p className="text-slate-400 text-[10px] font-black mt-2 uppercase tracking-[2px] flex items-center gap-1.5 leading-none">
                <Banknote size={12} className="text-[#363C90]" /> Secure Payment Processing
              </p>
           </div>
           <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-400 hover:text-[#363C90] text-[10px] font-black uppercase tracking-widest transition-colors">
              <ChevronLeft size={14} /> Back
           </button>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT: PAYMENT OPTIONS & FORM */}
          <div className="lg:col-span-7 space-y-6">
             <div className="grid grid-cols-3 gap-3">
                {methods.map((method) => {
                  const isActive = selected === method.id;
                  return (
                    <button
                      key={method.id}
                      onClick={() => setSelected(method.id)}
                      className={`flex flex-col items-center p-5 rounded-2xl border-2 transition-all duration-200 
                        ${isActive 
                          ? "bg-white border-[#363C90] shadow-md ring-1 ring-[#363C90]" 
                          : "bg-white border-slate-100 hover:border-slate-200 shadow-sm"}`}
                    >
                      <div className={`p-2.5 rounded-xl mb-3 ${isActive ? "bg-indigo-50 text-[#363C90]" : method.bg + " " + method.color}`}>
                        <method.icon size={20} />
                      </div>
                      <span className={`text-[11px] font-black uppercase tracking-tight ${isActive ? "text-slate-900" : "text-slate-500"}`}>
                        {method.label}
                      </span>
                    </button>
                  );
                })}
             </div>

             <div className="bg-white rounded-[28px] p-8 border border-slate-100 shadow-sm min-h-[300px] flex flex-col justify-center">
                {!selected ? (
                  <div className="text-center space-y-3 opacity-30 py-10">
                    <Info size={40} className="mx-auto text-slate-400" />
                    <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">Select a payment mode above</p>
                  </div>
                ) : (
                  <div className="animate-in fade-in duration-500">
                    {selected === "UPI" && (
                      <div className="space-y-4">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">UPI ID (VPA)</label>
                        <input 
                          type="text"
                          placeholder="e.g. user@okaxis"
                          className="w-full bg-slate-50 border-2 border-transparent focus:border-[#363C90] focus:bg-white p-4 rounded-2xl outline-none font-bold text-sm transition-all"
                          value={upiId}
                          onChange={(e) => setUpiId(e.target.value)}
                        />
                        <p className="text-[9px] text-slate-400 font-bold italic px-1">* Amount will be credited instantly after pickup.</p>
                      </div>
                    )}

                    {selected === "Bank Transfer" && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div className="space-y-2">
                              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Account Holder</label>
                              <input 
                                placeholder="Full Name" 
                                className="w-full bg-slate-50 border-2 border-transparent focus:border-[#363C90] focus:bg-white p-4 rounded-xl outline-none font-bold text-xs transition-all"
                                value={accHolder}
                                onChange={(e) => setAccHolder(e.target.value)}
                              />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Account Number</label>
                              <input 
                                placeholder="0000 0000 0000" 
                                className="w-full bg-slate-50 border-2 border-transparent focus:border-[#363C90] focus:bg-white p-4 rounded-xl outline-none font-bold text-xs transition-all"
                                value={accNumber}
                                onChange={(e) => setAccNumber(e.target.value)}
                              />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Bank Name</label>
                              <input 
                                placeholder="State Bank of India" 
                                className="w-full bg-slate-50 border-2 border-transparent focus:border-[#363C90] focus:bg-white p-4 rounded-xl outline-none font-bold text-xs transition-all"
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                              />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">IFSC Code</label>
                              <input 
                                placeholder="SBIN0001234" 
                                className="w-full bg-slate-50 border-2 border-transparent focus:border-[#363C90] focus:bg-white p-4 rounded-xl outline-none font-bold text-xs transition-all uppercase"
                                value={ifsc}
                                onChange={(e) => setIfsc(e.target.value)}
                              />
                           </div>
                        </div>
                      </div>
                    )}

                    {selected === "Cash" && (
                      <div className="text-center py-6 space-y-4">
                        <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                          <Banknote size={28} />
                        </div>
                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">On-Spot Cash Payment</h3>
                        <p className="text-[11px] text-slate-500 font-bold leading-relaxed max-w-[280px] mx-auto italic">
                          Our executive will verify the device and pay you in cash immediately after inspection.
                        </p>
                      </div>
                    )}
                  </div>
                )}
             </div>
          </div>

          {/* RIGHT: DARK SUMMARY CARD */}
          <div className="lg:col-span-5 lg:sticky lg:top-8 space-y-4">
             <div className="bg-slate-900 rounded-[32px] p-8 text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-[-20px] right-[-20px] w-40 h-40 bg-white/5 rounded-full blur-3xl group-hover:bg-white/10 transition-all"></div>
               
               <p className="text-[10px] font-black uppercase tracking-[3px] text-slate-500 mb-2">Final Payout Amount</p>
               <div className="flex justify-between items-baseline mb-8">
                  <h2 className="text-4xl font-black tracking-tight">₹{Number(params.finalPrice || 0).toLocaleString()}</h2>
                  <div className="flex items-center gap-1.5 text-emerald-400 text-[9px] font-black bg-emerald-400/10 px-3 py-1.5 rounded-full border border-emerald-400/20 uppercase tracking-widest">
                    <ShieldCheck size={12} /> Secure
                  </div>
               </div>

               <div className="space-y-3 pt-6 border-t border-white/10">
                  <div className="flex justify-between text-[11px] font-bold text-slate-400">
                    <span>Pickup Slot</span>
                    <span className="text-white">{params.pickupSlot?.split('(')[0] || 'Scheduled'}</span>
                  </div>
                  <div className="flex justify-between text-[11px] font-bold text-slate-400">
                    <span>Payment Mode</span>
                    <span className="text-white font-black uppercase tracking-wider">{selected || 'Not Selected'}</span>
                  </div>
               </div>

               <button
                 disabled={!selected || !isFormValid() || loading}
                 onClick={confirmSell}
                 className="w-full mt-8 py-5 bg-white text-slate-900 rounded-2xl font-black text-[11px] flex items-center justify-center gap-3 disabled:opacity-20 shadow-xl hover:bg-slate-100 transition-all uppercase tracking-[2px]"
               >
                 {loading ? <Loader2 className="animate-spin" size={18} /> : <>CONFIRM ORDER <ArrowRight size={18} /></>}
               </button>
             </div>

             <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3">
                <Info size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[9px] font-black text-amber-800 uppercase leading-relaxed tracking-wide">
                  By confirming, you agree to our terms of service regarding device ownership and condition.
                </p>
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Payment;